<script setup lang="ts">
import { computed, ref, useSlots } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { RoutePlan } from '@/api/route-plans';
import type { AssignmentsByCaregiver } from '@/composables/useAssignmentsByCaregiver';
import { useRoutePlanViewStore, type RoutePlanViewTab } from '@/stores/route-plan-view';
import RoutePlanMap from './route-plan-map.vue';
import RoutePlanTimeline from './route-plan-timeline.vue';
import RoutePlanCaregiverRoutes from './route-plan-caregiver-routes.vue';

const { t } = useI18n();

interface Props {
  routePlan: RoutePlan;
  assignmentsByCaregiver: AssignmentsByCaregiver;
  colorMap: Record<number, string>;
  readOnly?: boolean;
}

withDefaults(defineProps<Props>(), { readOnly: false });
const emit = defineEmits<{
  'update-assignment': [payload: { id: number; status: 'accepted' | 'rejected' }];
}>();

// The interactive calendar is provided by the parent (the board) through the #calendar slot;
// screens without it (the applied view) just show map/timeline/list.
const slots = useSlots();
const hasCalendar = computed(() => !!slots.calendar);

// Tab choice persists across Rails navigations; selection is view-local. When the persisted
// tab is 'calendar' but this instance has no calendar slot, fall back to the map for display
// without overwriting the stored preference.
const { lastTab } = storeToRefs(useRoutePlanViewStore());
const activeTab = computed<RoutePlanViewTab>({
  get: () => ((lastTab.value === 'calendar' && !hasCalendar.value) ? 'map' : lastTab.value),
  set: (value) => { lastTab.value = value; },
});
const selectedCaregiverId = ref<number | null>(null);

function onSelectCaregiver(id: number) {
  selectedCaregiverId.value = selectedCaregiverId.value === id ? null : id;
}
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-2">
      <v-tabs
        v-model="activeTab"
        density="compact"
        color="primary"
        class="flex-grow-1"
      >
        <v-tab
          v-if="hasCalendar"
          value="calendar"
        >
          {{ t('routePlan.viewTabs.calendar') }}
        </v-tab>
        <v-tab value="map">
          {{ t('routePlan.viewTabs.map') }}
        </v-tab>
        <v-tab value="timeline">
          {{ t('routePlan.viewTabs.timeline') }}
        </v-tab>
        <v-tab value="list">
          {{ t('routePlan.viewTabs.list') }}
        </v-tab>
      </v-tabs>
      <!-- Trailing toolbar actions provided by the parent (e.g. the board's coverage button). -->
      <slot name="append" />
    </div>

    <div v-show="activeTab === 'calendar'">
      <slot name="calendar" />
    </div>

    <div
      v-show="activeTab === 'map'"
      class="position-relative mb-4 rounded-lg overflow-hidden map-container"
    >
      <route-plan-map
        :assignments-by-caregiver="assignmentsByCaregiver"
        :route-plan-caregivers="routePlan.routePlanCaregivers || []"
        :highlighted-caregiver-id="selectedCaregiverId"
        :color-map="colorMap"
        :caregiver-existing-shifts="routePlan.caregiverExistingShifts || {}"
      />
      <v-card
        class="position-absolute pa-2 map-legend"
        density="compact"
        elevation="2"
      >
        <div
          v-for="(group, cgId) in assignmentsByCaregiver"
          :key="cgId"
          class="d-flex align-center gap-3 py-1 cursor-pointer"
          @click="onSelectCaregiver(Number(cgId))"
        >
          <div
            class="rounded-circle flex-shrink-0 color-dot-sm"
            :style="{ backgroundColor: colorMap[Number(cgId)] }"
          />
          <span class="text-caption ml-1">{{ group.name }} ({{ group.assignments.length }})</span>
        </div>
      </v-card>
    </div>

    <route-plan-timeline
      v-show="activeTab === 'timeline'"
      :assignments-by-caregiver="assignmentsByCaregiver"
      :color-map="colorMap"
      :selected-caregiver-id="selectedCaregiverId"
      :caregiver-breaks="routePlan.caregiverBreaks || {}"
      :caregiver-existing-shifts="routePlan.caregiverExistingShifts || {}"
      class="mb-4"
      @select-caregiver="onSelectCaregiver"
    />

    <route-plan-caregiver-routes
      v-show="activeTab === 'list'"
      :route-plan="routePlan"
      :assignments-by-caregiver="assignmentsByCaregiver"
      :color-map="colorMap"
      :selected-caregiver-id="selectedCaregiverId"
      :read-only="readOnly"
      class="mb-4"
      @select-caregiver="onSelectCaregiver"
      @update-assignment="emit('update-assignment', $event)"
    />
  </div>
</template>

<style scoped>
.map-container {
  height: 400px;
}

.map-legend {
  bottom: 12px;
  left: 12px;
  z-index: 1000;
  opacity: .9;
  max-height: 300px;
  overflow-y: auto;
}

.color-dot-sm {
  width: 10px;
  height: 10px;
}
</style>
